import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from "@/views/academy/api/apiV1Requests";
import { PaymentsApiEndpoints } from "@/views/payments/PaymentsApiEndpoints";
import { prepareQueryParams } from "@/helpers/prepareQueryParams";
import {
  ApiRequestHeaderTypes,
  ApiRequestHeaderValueTypes
} from "@/views/academy/enums/ApiRequestHeaderTypes";

const state = () => ({
  feeAdminTeams: [],
  players: [],
  feesSearchCriteria: {}
});

const getters = {
  feeAdminTeams: (state: { feeAdminTeams: [] }) => state.feeAdminTeams,
  players: (state: { players: [] }) => state.players,
  feesSearchCriteria: (state: { feesSearchCriteria: object }) =>
    state.feesSearchCriteria
};

const mutations = {
  setFeeAdminTeams: (
    state: { feeAdminTeams: [] },
    payload: { feeAdminTeams: [] }
  ) => {
    state.feeAdminTeams = payload.feeAdminTeams;
  },
  setPlayers: (state: { players: [] }, payload: { players: [] }) => {
    state.players = payload.players;
  },
  setFeesSearchCriteria: (
    state: { feesSearchCriteria: object },
    payload: { criteria: object }
  ) => {
    state.feesSearchCriteria = payload.criteria;
  }
};

const actions = {
  fetchFeesList: async (
    context: any,
    payload: {
      criteria: {
        page: number;
        filter: string;
        status: string;
        showAllTeams: boolean;
        orgUnitId: string;
        playerId: string;
        organizationId: string;
      };
      successCallback: Function;
      failureCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.fetchFeesList");
    const { criteria, successCallback, failureCallback } = payload;
    const query = prepareQueryParams({
      pageSize: 50,
      page: criteria?.page || 0,
      filter: criteria?.filter ? criteria?.filter?.trim() : "",
      status: criteria?.status || "",
      showAllTeams: criteria?.showAllTeams || "",
      orgUnitId: criteria?.orgUnitId || null,
      organizationId: criteria?.organizationId || null
    });

    try {
      const res: any = await getRequest(
        `${PaymentsApiEndpoints.FETCH_PLAYER_FEES}${query}`,
        {
          timeout: 10000
        }
      );
      context.commit("setFeesSearchCriteria", { criteria });
      return successCallback && successCallback(res);
    } catch (e) {
      console.error(e);
      return failureCallback && failureCallback();
    }
  },
  fetchPaymentAccounts: async (
    context: any,
    payload: {
      organizationId: string;
      successCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.fetchPaymentAccounts");
    const { successCallback, organizationId } = payload;
    try {
      const res: any = await getRequest(
        PaymentsApiEndpoints.FETCH_PAYMENT_ACCOUNTS + `/${organizationId}`
      );

      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  },
  createFee: async (
    context: any,
    payload: {
      body: object;
      successCallback: Function;
      failCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.createFee");
    const { body, successCallback, failCallback } = payload;
    try {
      const res: any = await postRequest(PaymentsApiEndpoints.CREATE_FEE, body);

      //console.log(res);

      if (res.success) {
        successCallback && successCallback(res.data);
      } else {
        failCallback && failCallback(res.data);
      }
    } catch (e) {
      console.error(e);
      failCallback && failCallback({ success: false, exception: e });
    }
  },
  updateFee: async (
    context: any,
    payload: {
      id: string;
      orgUnitId: string;
      body: object;
      successCallback: Function;
      failureCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.updateFee");
    //console.log(payload.body);
    const { id, body, successCallback, failureCallback } = payload;
    try {
      const res: any = await postRequest(
        `${PaymentsApiEndpoints.UPDATE_FEE}${payload.orgUnitId}/updatePlayerFee`,
        body
      );
      successCallback && successCallback(res);
    } catch (e) {
      console.error(e);
      failureCallback && failureCallback("Error updating fee.", e);
    }
  },
  deleteFee: async (
    context: any,
    payload: {
      id: string;
      successCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.deleteFee");
    const { id, successCallback } = payload;
    try {
      const res: any = await deleteRequest(
        `${PaymentsApiEndpoints.DELETE_FEE}/${id}`
      );

      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  },
  cancelFee: async (
    context: any,
    payload: {
      id: string;
      successCallback: Function;
    }
  ) => {
    const { id, successCallback } = payload;
    console.log("Call to cancel the scheduled payment here!", id);

    try {
      const res: any = await postRequest(
        `${PaymentsApiEndpoints.CANCEL_FEE}/${id}`
      );

      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  },
  exportPlayersFeesCSV: async (
    context: any,
    payload: {
      orgUnitId: string;
      fromDate: string;
      toDate: string;
      dateType: string;
      includeTeams: boolean;
    }
  ) => {
    //console.log("entering PaymentsModule.exportPlayersFeesCSV");
    try {
      const body = {
        includeTeams: true,
        to: "31/07/2022",
        from: "31/07/2020",
        dateType: "unpaid"
      };
      window.open(
        `${PaymentsApiEndpoints.EXPORT_FEES_CSV}${payload.orgUnitId}/downloadPlayerFeesExport?to=${payload.toDate}&from=${payload.fromDate}&dateType=${payload.dateType}&includeTeams=${payload.includeTeams}`,
        "_blank"
      );
    } catch (e) {
      console.error(e);
    }
  },

  fetchFeeAdminTeams: async (context: any) => {
    //console.log("entering PaymentsModule.fetchFeeAdminTeams");

    try {
      const res: any = await getRequest(
        PaymentsApiEndpoints.FETCH_FEE_ADMIN_TEAMS
      );

      if (res.status === "success") {
        context.commit("setFeeAdminTeams", { feeAdminTeams: res.data });
      }
    } catch (e) {
      console.error(e);
    }
  },
  fetchPlayers: async (context: any) => {
    //console.log("entering PaymentsModule.fetchPlayers");
    try {
      const res: any = await getRequest(PaymentsApiEndpoints.FETCH_PLAYERS);
      if (res.status === "success") {
        context.commit("setPlayers", { players: res.data });
      }
    } catch (e) {
      console.error(e);
    }
  },
  createStripePaymentIntent: async (
    context: any,
    payload: {
      feePlayerId: string;
      successCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.createStripePaymentIntent");

    const { feePlayerId, successCallback } = payload;
    try {
      const res: any = await postRequest(
        `${PaymentsApiEndpoints.CREATE_STRIPE_PAYMENT_INTENT}/${feePlayerId}`
      );
      successCallback && successCallback(res.data);
    } catch (e) {
      console.error(e);
    }
  },
  fetchFeeDetails: async (
    context: any,
    payload: {
      feePlayerId: string;
      userPickedPaymentPlan: string;
      successCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.fechFeeDetails");

    const { feePlayerId, userPickedPaymentPlan, successCallback } = payload;
    try {
      const query = prepareQueryParams({ userPickedPaymentPlan });
      const res: any = await postRequest(
        `${PaymentsApiEndpoints.FETCH_FEE_DETAILS}/${feePlayerId}${query}`
      );
      successCallback && successCallback(res.data);
    } catch (e) {
      console.error(e);
    }
  },
  fetchFee: async (
    context: any,
    payload: {
      feeId: string;
    }
  ) => {
    //console.log("entering PaymentsModule.fetchFee");

    const { feeId } = payload;
    try {
      const res: any = await getRequest(
        `${PaymentsApiEndpoints.FETCH_FEE}/${feeId}`
      );
      return res;
    } catch (e) {
      console.error(e);
    }
  },
  fetchFeePaymentDetails: async (
    context: any,
    payload: {
      body: {
        amountInCents: string;
        organizationId: string;
        absorbFees: boolean;
        paymentInstallmentForm: object;
        paymentPlanEnabled: boolean;
      };
      successCallback: Function;
    }
  ) => {
    //console.log("entering PaymentsModule.fetchFeePaymentDetails");

    const { body, successCallback } = payload;
    try {
      const res: any = await postRequest(
        PaymentsApiEndpoints.FETCH_FEE_PAYMENT_DETAILS,
        body
      );
      successCallback && successCallback(res.data);
    } catch (e) {
      console.error(e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
